/* ── WORK_TRACK — Meet (WebRTC video calls) ───────────────── */

import {
  $,
  $$,
  esc,
  icon,
  state,
  meet,
  api,
  toast,
  registerView,
  openModal,
  closeModal,
  avatarHtml,
  activeMembers,
  emptyHtml,
  ROLE_LABEL,
  setOnViewChanged,
  go
} from './core';
import type { PeerState } from './core';
import { loadChat, renderChatMsg } from './chat';
import { sendComposerMessage, setupChatTextarea, setMeetChatReconnect } from './composer';
import { attachMentionAutocomplete } from './mentions';
import type { ChatWSInbound, MeetingHistoryMsg, MeetingInvite, MeetingRoom, MeetWSInbound, PeerMeta } from './types';

const MEET_ICE: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
const MEET_RING_TIMEOUT = 20000;

let meetReconnectTries = 0;
let ringAudio: HTMLAudioElement | null = null;
let ringTimer: number | null = null;
let broadcastChannel: BroadcastChannel | null = null;

function initBroadcastChannel(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    broadcastChannel = new BroadcastChannel('worktrack_meet');
    broadcastChannel.onmessage = (ev) => {
      if (ev.data?.type === 'incoming_call' && state.user?.id !== ev.data.fromId) {
        const invite = ev.data.invite as MeetingInvite;
        if (invite && !meet._notifiedInviteIds.includes(invite.id)) {
          showCallPopup(invite);
          playRingTone();
        }
      } else if (ev.data?.type === 'call_cancelled') {
        if (currentCall && currentCall.id === ev.data.inviteId) closeCallPopup();
        stopRingTone();
      }
    };
    // Signal ready
    broadcastChannel.postMessage({ type: 'ready' });
  } catch {
    /* BroadcastChannel not available */
  }

  // Fallback: localStorage-based cross-tab notification
  window.addEventListener('storage', (e) => {
    if (e.key === 'wt_meet_call' && e.newValue) {
      try {
        const data = JSON.parse(e.newValue);
        if (data.type === 'incoming_call' && state.user?.id !== data.fromId) {
          const invite = data.invite as MeetingInvite;
          if (invite && !meet._notifiedInviteIds.includes(invite.id)) {
            showCallPopup(invite);
            playRingTone();
          }
        } else if (data.type === 'call_cancelled') {
          if (currentCall && currentCall.id === data.inviteId) closeCallPopup();
          stopRingTone();
        }
      } catch {
        /* ignore parse errors */
      }
    }
  });
}

function broadcastIncomingCall(invite: MeetingInvite): void {
  if (broadcastChannel) {
    // Ensure the invite is a plain object for structured clone
    const plainInvite = {
      id: invite.id,
      room: invite.room,
      from_user: invite.from_user,
      to_user: invite.to_user,
      from_name: invite.from_name,
      from_username: invite.from_username,
      from_avatar: invite.from_avatar,
      status: invite.status,
      created_at: invite.created_at
    };
    broadcastChannel.postMessage({
      type: 'incoming_call',
      fromId: state.user?.id,
      invite: plainInvite
    });
  }
  // Also use localStorage as fallback
  try {
    localStorage.setItem('wt_meet_call', JSON.stringify({
      type: 'incoming_call',
      fromId: state.user?.id,
      invite: {
        id: invite.id,
        room: invite.room,
        from_user: invite.from_user,
        to_user: invite.to_user,
        from_name: invite.from_name,
        from_username: invite.from_username,
        from_avatar: invite.from_avatar,
        status: invite.status,
        created_at: invite.created_at
      },
      timestamp: Date.now()
    }));
  } catch {
    /* ignore */
  }
}

function broadcastCallCancelled(inviteId: number): void {
  if (broadcastChannel) {
    broadcastChannel.postMessage({
      type: 'call_cancelled',
      inviteId
    });
  }
  // Also use localStorage as fallback
  try {
    localStorage.setItem('wt_meet_call', JSON.stringify({
      type: 'call_cancelled',
      inviteId,
      timestamp: Date.now()
    }));
  } catch {
    /* ignore */
  }
}

function playRingTone(): void {
  stopRingTone();
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.15);
    osc.frequency.setValueAtTime(440, ctx.currentTime + 0.3);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.45);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.5);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 1.5);
    ringTimer = window.setTimeout(() => playRingTone(), 2000);
  } catch {
    /* fallback to audio element */
    ringAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQcAAACAgICAgICAAACAgICAgICAAACAgICAgICAAACAgICAgICAAACAgICAgICAAACAgICAgICAAACAgICAgICAAACAgICAgICAAACAgICAgICA=');
    ringAudio.loop = true;
    ringAudio.volume = 0.3;
    ringAudio.play().catch(() => {});
  }
}

function stopRingTone(): void {
  if (ringTimer) {
    clearTimeout(ringTimer);
    ringTimer = null;
  }
  if (ringAudio) {
    ringAudio.pause();
    ringAudio = null;
  }
}

function meetRoomLabel(name: string): string {
  const m = name.match(/^direct-(\d+)-(\d+)$/);
  if (!m) return name;
  const me = state.user?.id;
  const peerId = [Number(m[1]), Number(m[2])].find(id => id !== me) || Number(m[1]);
  const peer = state.members.find(mem => mem.id === peerId);
  return peer ? `Call with ${peer.name || peer.username}` : 'Direct call';
}

export async function loadMeet(): Promise<void> {
  try {
    const [rooms, invites] = await Promise.all([
      api<MeetingRoom[]>('/api/meetings'),
      api<MeetingInvite[]>('/api/meetings/invites').catch(() => [])
    ]);
    state.meetings = rooms || [];
    meet.myInvites = invites || [];
    // keep the Chat tab's meeting channels in sync
    if (state.view === 'chat') loadChat();
    renderMeetRooms(rooms || []);
    renderMeetMembers();
    renderMeetInvites(invites || []);
  } catch (e) {
    toast((e as Error).message, true);
  }
}

function renderMeetRooms(rooms: MeetingRoom[]): void {
  $('#meetRoomCount').textContent = `${rooms.length} rooms`;
  const ul = $('#meetRoomList');
  if (!rooms.length) {
    ul.innerHTML = emptyHtml(icon('meet', 30), 'No rooms yet', 'Create a room above, or call a teammate directly.');
    return;
  }
  ul.innerHTML = rooms
    .map(r => {
      const canDelete = r.created_by === state.user?.id || state.user?.role === 'owner' || state.user?.role === 'admin';
      return `
    <li>
      <span class="item-icon">${icon('meet', 18)}</span>
      <div class="item-main">
        <div class="item-title">${esc(meetRoomLabel(r.name))} ${r.no_audio ? `<span class="role-chip" title="Video only">${icon('muteMic', 13)} no audio</span>` : ''}</div>
        <div class="item-sub">Created ${esc(new Date(r.created_at).toLocaleDateString())}${r.created_by === state.user?.id ? ' · by you' : ''}</div>
      </div>
      <div class="item-right tc-actions">
        <button class="btn btn-ghost btn-sm" data-joinroom="${esc(r.name)}">Join</button>
        ${canDelete ? `<button class="btn btn-ghost btn-sm" data-rooms-delete="${esc(r.name)}" title="Delete room">${icon('trash', 15)}</button>` : ''}
        <button class="btn btn-ghost btn-sm" data-rooms-history="${esc(r.name)}" title="View saved chat history">${icon('clock', 15)}</button>
      </div>
    </li>`;
    })
    .join('');
  $$<HTMLElement>('#meetRoomList [data-joinroom]').forEach(btn =>
    btn.addEventListener('click', () => enterRoom(btn.dataset.joinroom || ''))
  );
  $$<HTMLElement>('#meetRoomList [data-rooms-history]').forEach(btn =>
    btn.addEventListener('click', () => openMeetHistory(btn.dataset.roomsHistory || ''))
  );
  $$<HTMLElement>('#meetRoomList [data-rooms-delete]').forEach(btn =>
    btn.addEventListener('click', async () => {
      if (!confirm(`Delete the room "${btn.dataset.roomsDelete}"? Members can still join from an open invite/link.`))
        return;
      try {
        await api(`/api/meetings/${encodeURIComponent(btn.dataset.roomsDelete || '')}`, 'DELETE');
        toast('Room deleted');
        loadMeet();
      } catch (err) {
        toast((err as Error).message, true);
      }
    })
  );
}

function renderMeetMembers(): void {
  const members = activeMembers().filter(m => m.id !== state.user?.id);
  $('#meetMemberCount').textContent = `${members.length} teammates`;
  const ul = $('#meetMemberList');
  if (!members.length) {
    ul.innerHTML = emptyHtml(
      icon('users', 30),
      'No other members',
      'Invite teammates to be able to call them directly.'
    );
    return;
  }
  ul.innerHTML = members
    .map(m => {
      const n = m.name || m.username;
      return `<li>
      ${avatarHtml(n.slice(0, 2).toUpperCase(), m.id, m.avatar || null)}
      <div class="item-main">
        <div class="item-title">@${esc(m.username || '')}</div>
        <div class="item-sub">${esc(n)}${m.email ? ' · ' + esc(m.email) : ''} · ${esc(ROLE_LABEL[m.role] || m.role)}</div>
      </div>
      <div class="item-right"><button class="btn btn-primary btn-sm" data-callmember="${m.id}">${icon('phone', 14)} Call</button></div>
    </li>`;
    })
    .join('');
  $$<HTMLElement>('#meetMemberList [data-callmember]').forEach(btn =>
    btn.addEventListener('click', () => callMember(Number(btn.dataset.callmember)))
  );
}

function renderMeetInvites(invites: MeetingInvite[]): void {
  const ul = $('#meetInviteList');
  meet._seenInviteIds = invites.map(i => i.id);
  if (!invites.length) {
    ul.innerHTML = emptyHtml(
      icon('bell', 30),
      'No incoming calls',
      'When a teammate calls you, the call appears here — just answer.'
    );
    return;
  }
  ul.innerHTML = invites
    .map(
      i => `
    <li>
      <span class="item-icon">${icon('phone', 18)}</span>
      <div class="item-main">
        <div class="item-title">${esc(i.from_name || 'A teammate')} is calling</div>
        <div class="item-sub">Room: ${esc(meetRoomLabel(i.room))}</div>
      </div>
      <div class="item-right tc-actions">
        <button class="btn btn-primary btn-sm" data-joininvite="${i.id}" data-room="${esc(i.room)}">Answer</button>
        <button class="btn btn-ghost btn-sm" data-declineinvite="${i.id}">Decline</button>
      </div>
    </li>`
    )
    .join('');
  $$<HTMLElement>('#meetInviteList [data-joininvite]').forEach(btn =>
    btn.addEventListener('click', async () => {
      try {
        await api(`/api/meetings/invites/${btn.dataset.joininvite}`, 'POST');
      } catch {
        /* ignore */
      }
      enterRoom(btn.dataset.room || '');
    })
  );
  $$<HTMLElement>('#meetInviteList [data-declineinvite]').forEach(btn =>
    btn.addEventListener('click', async () => {
      try {
        await api(`/api/meetings/invites/${btn.dataset.declineinvite}`, 'DELETE');
      } catch {
        /* ignore */
      }
      toast('Call declined');
      loadMeet();
    })
  );
}

async function openMeetHistory(name: string): Promise<void> {
  const card = openModal(`
    <div class="modal-head"><h2>Saved chat history — ${esc(name)}</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
    <div class="chat-messages meet-history" id="meetHistory" style="max-height:60vh;overflow-y:auto"></div>`);
  const el = card.querySelector<HTMLElement>('#meetHistory');
  if (!el) return;
  el.innerHTML = '<div class="chat-empty">Loading…</div>';
  try {
    const hist = await api<MeetingHistoryMsg[]>(`/api/meetings/${encodeURIComponent(name)}/history`);
    if (!hist.length) {
      el.innerHTML = '<div class="chat-empty">No messages were shared in this meeting.</div>';
      return;
    }
    el.innerHTML = hist.map(m => renderChatMsg({ ...m, channel: name })).join('');
  } catch {
    el.innerHTML = '<div class="chat-empty">Could not load history.</div>';
  }
}

async function callMember(id: number): Promise<void> {
  const a = state.user?.id,
    b = Number(id);
  if (!a || !b || a === b) {
    toast('Pick a teammate to call', true);
    return;
  }
  const room = `direct-${Math.min(a, b)}-${Math.max(a, b)}`;
  try {
    await api('/api/meetings', 'POST', { name: room });
    await api(`/api/meetings/${encodeURIComponent(room)}/invites`, 'POST', { to_user: b });
    toast('Calling… you are in the room');
  } catch (e) {
    toast((e as Error).message, true);
  }
  meet.prevView = state.view;
  meet.ringRoom = room;
  go('meet');
  enterRoom(room);
  meet.ringTimer = window.setTimeout(() => {
    if (meet.ringRoom === room && meet.peers.size === 0) {
      meet.ringRoom = null;
      toast('No one answered — call ended', 'info');
      leaveRoom();
      clearMeetChat(room);
      returnToPrevView();
    }
  }, MEET_RING_TIMEOUT);
}

const meetJoinForm = $<HTMLFormElement>('#meetJoinForm');
meetJoinForm.addEventListener('submit', e => {
  e.preventDefault();
  const f = new FormData(meetJoinForm);
  const name = String(f.get('room') || '').trim();
  if (!name) return;
  enterRoom(name);
});

function resumeAudioContext(): void {
  if (meet.audioCtx && meet.audioCtx.state === 'suspended') {
    meet.audioCtx.resume().catch(() => {});
  }
  $('#meetGrid').querySelectorAll<HTMLVideoElement>('video').forEach(v => {
    if (v.paused) v.play().catch(() => {});
  });
}

async function enterRoom(name: string): Promise<void> {
  if (!name) return;
  const seq = meet.enterSeq;
  let roomInfo: Partial<MeetingRoom> | null = null;
  try {
    roomInfo = await api<MeetingRoom>('/api/meetings', 'POST', {
      name,
      no_audio: false
    });
  } catch {
    roomInfo = { name };
  }
  if (seq !== meet.enterSeq) return;
  meet.noAudio = !!(roomInfo && roomInfo.no_audio);

  if (!meet.localStream) {
    const constraints: MediaStreamConstraints = { video: true, audio: !meet.noAudio };
    try {
      meet.localStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch {
      meet.localStream = null;
      toast('Camera/mic unavailable — you can still join without it and enable it from the controls', true);
    }
    if (seq !== meet.enterSeq) {
      if (meet.localStream) {
        meet.localStream.getTracks().forEach(t => t.stop());
        meet.localStream = null;
      }
      return;
    }
  }
  meet.media = {
    mic: !!(meet.localStream && meet.localStream.getAudioTracks().length),
    cam: !!(meet.localStream && meet.localStream.getVideoTracks().length)
  };
  if (meet.noAudio) meet.media.mic = false;

  meet.room = name;
  meet.requests = [];
  meet.waitingApproval = false;
  $('#meetLobby').classList.add('hidden');
  $('#meetRoom').classList.remove('hidden');
  $('#meetRoomName').textContent = meetRoomLabel(name);
  $('#meetOnline').textContent = 'Connecting…';
  $('#meetWaitingOverlay').classList.add('hidden');
  hideApprovalPanel();
  $('#meetMic').classList.toggle('hidden', meet.noAudio);
  $('#meetMic').classList.toggle('off', !meet.media.mic);
  $('#meetCam').classList.toggle('off', !meet.media.cam);
  $('#meetHand').classList.remove('off');
  renderMeetSelf();
  connectMeetChat(name.trim());

  // Resume audio context on user interaction (browser autoplay policy)
  const meetRoomEl = $('#meetRoom');
  const resumeHandler = () => {
    resumeAudioContext();
    meetRoomEl.removeEventListener('click', resumeHandler);
  };
  meetRoomEl.addEventListener('click', resumeHandler);

  meetReconnectTries = 0;
  try {
    await connectMeetWS(name);
  } catch {
    /* scheduleMeetReconnect handles retries */
  }
}

function connectMeetWS(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/api/meeting?token=${encodeURIComponent(state.token || '')}&room=${encodeURIComponent(name)}`;
    const ws = new WebSocket(url);
    const timer = window.setTimeout(() => reject(new Error('socket timeout')), 8000);
    ws.onopen = () => {
      clearTimeout(timer);
      resolve();
    };
    ws.onerror = () => {
      clearTimeout(timer);
      reject(new Error('socket error'));
    };
    ws.onmessage = ev => {
      let m: MeetWSInbound;
      try {
        m = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      if (m.type === 'joined') meetReconnectTries = 0;
      handleMeetMsg(m);
    };
    ws.onclose = () => {
      if (meet.room && meet.ws === ws) scheduleMeetReconnect();
    };
    meet.ws = ws;
  });
}

function scheduleMeetReconnect(): void {
  if (!meet.room) return;
  meetReconnectTries++;
  if (meetReconnectTries > 5) {
    meetReconnectTries = 0;
    toast('Lost connection to the meeting', true);
    leaveRoom();
    return;
  }
  $('#meetOnline').textContent = 'Reconnecting…';
  window.setTimeout(async () => {
    if (!meet.room) return;
    meet.peers.forEach(s => {
      try {
        s.pc.close();
      } catch {
        /* ignore */
      }
    });
    meet.peers.clear();
    meet.audioLevels.clear();
    $('#meetGrid')
      .querySelectorAll('.meet-tile:not([data-peer="self"])')
      .forEach(t => t.remove());
    try {
      await connectMeetWS(meet.room);
      toast('Reconnected', 'info');
    } catch {
      scheduleMeetReconnect();
    }
  }, 1500);
}

function renderMeetSelf(): void {
  const grid = $('#meetGrid');
  if (!grid.querySelector('[data-peer="self"]')) {
    grid.insertAdjacentHTML(
      'beforeend',
      `
      <div class="meet-tile self" data-peer="self">
        <video autoplay playsinline muted></video>
        <div class="meet-tile-name">You</div>
        <button type="button" class="mt-fs" title="Fullscreen">${icon('maximize', 14)}</button>
        <div class="meet-tile-badges">
          <span class="mt-badge mt-hand hidden">${icon('hand', 13)}</span>
          <span class="mt-badge mt-live hidden">LIVE</span>
          <span class="mt-badge mt-muted hidden">${icon('muteMic', 13)}</span>
          <span class="mt-badge mt-camoff hidden">${icon('detachCam', 13)}</span>
          <span class="mt-badge mt-share hidden">${icon('monitor', 13)}</span>
        </div>
      </div>`
    );
    const fs = grid.querySelector<HTMLElement>('[data-peer="self"] .mt-fs');
    if (fs)
      fs.addEventListener('click', () =>
        fullscreenTile(grid.querySelector<HTMLVideoElement>('[data-peer="self"] video'))
      );
  }
  const v = grid.querySelector<HTMLVideoElement>('[data-peer="self"] video');
  if (v && meet.localStream && !v.srcObject) v.srcObject = meet.localStream;
  updateSelfTile();
}

function updateSelfTile(): void {
  const tile = $('#meetGrid').querySelector<HTMLElement>('[data-peer="self"]');
  if (!tile) return;
  const nameEl = tile.querySelector<HTMLElement>('.meet-tile-name');
  if (nameEl) {
    if (meet.handUp) nameEl.innerHTML = `You · ${icon('hand', 14)}`;
    else nameEl.textContent = 'You';
  }
  const m = tile.querySelector<HTMLElement>('.mt-muted');
  if (m) m.classList.toggle('hidden', meet.media.mic);
  const c = tile.querySelector<HTMLElement>('.mt-camoff');
  if (c) c.classList.toggle('hidden', meet.media.cam);
  const h = tile.querySelector<HTMLElement>('.mt-hand');
  if (h) h.classList.toggle('hidden', !meet.handUp);
  const s = tile.querySelector<HTMLElement>('.mt-share');
  if (s) s.classList.toggle('hidden', !meet.sharing);
  const v = tile.querySelector<HTMLVideoElement>('video');
  if (v) v.srcObject = meet.sharing ? meet.shareStream : meet.localStream;
}

function clearRingTimer(): void {
  if (meet.ringTimer) {
    clearTimeout(meet.ringTimer);
    meet.ringTimer = null;
  }
}

function handleMeetMsg(msg: MeetWSInbound): void {
  if (msg.type === 'waiting') {
    meet.waitingApproval = true;
    $('#meetWaitingOverlay').classList.remove('hidden');
    $('#meetOnline').textContent = 'Waiting for organizer approval…';
    return;
  }
  if (msg.type === 'join-denied') {
    toast('The organizer did not approve your entry', true);
    leaveRoom();
    return;
  }
  if (msg.type === 'join-request') {
    if (meet.isOrganizer) {
      meet.requests.push({ from: msg.from, name: msg.name });
      renderApprovalPanel();
    }
    return;
  }
  if (msg.type === 'joined') {
    meet.selfId = msg.self.id;
    meet.organizerId = msg.organizer != null ? String(msg.organizer) : null;
    meet.isOrganizer = meet.organizerId === String(meet.selfId);
    meet.noAudio = !!msg.no_audio;
    meet.waitingApproval = false;
    $('#meetWaitingOverlay').classList.add('hidden');
    $('#meetMic').classList.toggle('hidden', meet.noAudio);
    $('#meetMuteAll').classList.toggle('hidden', !meet.isOrganizer);
    hideApprovalPanel();
    renderMeetSelf();
    resetSpeakerDetection();
    attachSelfMeter();
    $('#meetOnline').textContent = `${msg.peers.length} in room`;
    msg.peers.forEach(p => createPeer(p.id, p.name || '', offersNegotiation(meet.selfId ?? 0, p.id), p));
    if (msg.peers.length) {
      clearRingTimer();
      meet.ringRoom = null;
    }
  } else if (msg.type === 'peer-joined') {
    if (msg.peer.id !== meet.selfId) {
      clearRingTimer();
      meet.ringRoom = null;
      const existing = meet.peers.get(String(msg.peer.id));
      if (existing) {
        const cs = existing.pc.connectionState;
        if (cs === 'closed' || cs === 'failed') destroyPeer(msg.peer.id);
      }
      if (!meet.peers.has(String(msg.peer.id))) {
        addPeerTile(msg.peer);
        createPeer(msg.peer.id, msg.peer.name || '', offersNegotiation(meet.selfId ?? 0, msg.peer.id), msg.peer);
      }
    }
    $('#meetOnline').textContent = `${meet.peers.size + 1} in room`;
  } else if (msg.type === 'peer-left') {
    destroyPeer(msg.id);
  } else if (msg.type === 'peer-media') {
    updatePeerTile(msg.from, { mic: msg.mic, cam: msg.cam, hand: msg.hand, share: msg.share });
  } else if (msg.type === 'peer-hand') {
    updatePeerTile(msg.from, { hand: msg.hand });
  } else if (msg.type === 'hand-clear') {
    meet.handUp = false;
    $('#meetHand').classList.remove('off');
    updateSelfTile();
    $('#meetGrid')
      .querySelectorAll<HTMLElement>('.meet-tile')
      .forEach(t => {
        if (t.dataset.peer === 'self') updateSelfTile();
        else updatePeerTile(t.dataset.peer || '', { hand: false });
      });
  } else if (msg.type === 'mute-all') {
    if (!meet.isOrganizer) {
      meet.media.mic = false;
      if (meet.localStream)
        meet.localStream.getAudioTracks().forEach(t => {
          t.enabled = false;
        });
      $('#meetMic').classList.add('off');
      updateSelfTile();
      if (meet.ws) meet.ws.send(JSON.stringify({ type: 'media-state', from: meet.selfId, mic: false }));
      toast('The organizer muted your microphone', 'info');
    }
  } else if (msg.type === 'offer') {
    if (msg.to && msg.to !== meet.selfId) return;
    const pc = ensurePeer(msg.from, '…', false);
    pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: msg.sdp }))
      .then(() => pc.createAnswer())
      .then(ans => pc.setLocalDescription(ans))
      .then(() => {
        if (pc.localDescription && meet.ws)
          meet.ws.send(
            JSON.stringify({ type: 'answer', from: meet.selfId, to: msg.from, sdp: pc.localDescription.sdp })
          );
      })
      .then(() => flushIce(msg.from))
      .catch(e => console.error('answer err', e));
  } else if (msg.type === 'answer') {
    if (msg.to && msg.to !== meet.selfId) return;
    const pc = meet.peers.get(String(msg.from))?.pc;
    if (pc)
      pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: msg.sdp }))
        .then(() => flushIce(msg.from))
        .catch(() => {});
  } else if (msg.type === 'ice') {
    if (msg.to && msg.to !== meet.selfId) return;
    bufferIce(msg.from, JSON.parse(msg.candidate));
  }
}

function offersNegotiation(selfId: number, peerId: number): boolean {
  return Number(selfId) < Number(peerId);
}

function ensurePeer(id: number, name: string, isOfferer: boolean): RTCPeerConnection {
  const p = meet.peers.get(String(id));
  if (p) return p.pc;
  createPeer(id, name, isOfferer, null);
  return meet.peers.get(String(id))!.pc;
}

function createPeer(id: number, name: string, isOfferer: boolean, meta: PeerMeta | null): PeerState {
  const key = String(id);
  const pc = new RTCPeerConnection({ iceServers: MEET_ICE });
  const localForPeer = meet.sharing && meet.shareStream ? meet.shareStream : meet.localStream;
  const sentKinds = new Set<string>();
  if (localForPeer) {
    localForPeer.getTracks().forEach(t => {
      sentKinds.add(t.kind);
      pc.addTrack(t, localForPeer);
    });
  }
  ['audio', 'video'].forEach(kind => {
    if (!sentKinds.has(kind)) {
      try {
        pc.addTransceiver(kind as 'audio' | 'video', { direction: 'recvonly' });
      } catch {
        /* ignore */
      }
    }
  });
  const peerState: PeerState = {
    pc,
    name: name || '',
    ice: [],
    mic: !!(meta && meta.mic),
    cam: !!(meta && meta.cam),
    hand: !!(meta && meta.hand),
    stream: null
  };
  meet.peers.set(key, peerState);

  addPeerTile({ id, name: name || '…', mic: peerState.mic, cam: peerState.cam, hand: peerState.hand });

  pc.onicecandidate = e => {
    if (e.candidate && meet.ws) {
      meet.ws.send(JSON.stringify({ type: 'ice', from: meet.selfId, to: key, candidate: JSON.stringify(e.candidate) }));
    }
  };
  pc.ontrack = e => {
    const v = $('#meetGrid').querySelector<HTMLVideoElement>(`[data-peer="${key}"] video`);
    if (v) {
      v.srcObject = e.streams[0];
      // Ensure audio plays (browser autoplay policy)
      v.play().catch(() => {
        // If autoplay fails, user interaction will be needed
        console.log('Remote video autoplay failed, waiting for user interaction');
      });
      attachPeerMeter(key, e.streams[0]);
    }
    const st = meet.peers.get(key);
    if (st) st.stream = e.streams[0];
    if (meet.pipActive) renderPip();
  };
  pc.onconnectionstatechange = () => {
    const cs = pc.connectionState;
    const st = meet.peers.get(key);
    if (!st) return;
    if (cs === 'connected') {
      if (st.failTimer) {
        clearTimeout(st.failTimer);
        st.failTimer = null;
      }
      if (st.killTimer) {
        clearTimeout(st.killTimer);
        st.killTimer = null;
      }
    } else if (cs === 'disconnected') {
      // The peer's socket/media died abruptly (tab closed / network drop).
      // Give it a short grace period to recover, then drop the tile so a
      // frozen last frame doesn't linger on screen.
      if (!st.killTimer) {
        st.killTimer = window.setTimeout(() => {
          const cur = meet.peers.get(key);
          if (cur && (cur.pc.connectionState === 'disconnected' || cur.pc.connectionState === 'failed')) {
            destroyPeer(key);
          }
        }, 6000);
      }
    } else if (cs === 'failed') {
      // Transient ICE failures are common on flaky networks. Try an ICE
      // restart (from the lower-id side to avoid glare) before giving up.
      if (!st.failTimer) {
        st.failTimer = window.setTimeout(() => {
          st.failTimer = null;
          const now = meet.peers.get(key);
          if (!now) return;
          if (Number(meet.selfId) < Number(key)) {
            try {
              now.pc.restartIce();
            } catch {
              /* ignore */
            }
            window.setTimeout(() => renegotiate(now.pc, key), 150);
          }
          if (!st.killTimer) {
            st.killTimer = window.setTimeout(() => {
              const cur = meet.peers.get(key);
              if (cur && cur.pc.connectionState === 'failed') destroyPeer(key);
            }, 8000);
          }
        }, 3000);
      }
    } else if (cs === 'closed') {
      destroyPeer(key);
    }
  };

  if (isOfferer) {
    pc.createOffer()
      .then(o => pc.setLocalDescription(o))
      .then(() => {
        if (pc.localDescription && meet.ws)
          meet.ws.send(JSON.stringify({ type: 'offer', from: meet.selfId, to: key, sdp: pc.localDescription.sdp }));
      })
      .then(() => flushIce(key))
      .catch(e => console.error('offer err', e));
  }
  return peerState;
}

function bufferIce(id: number | string, candidate: RTCIceCandidateInit): void {
  const s = meet.peers.get(String(id));
  if (!s) return;
  const pc = s.pc;
  if (pc.remoteDescription) {
    pc.addIceCandidate(candidate).catch(() => {});
  } else {
    s.ice.push(candidate);
  }
}

function flushIce(id: number | string): void {
  const s = meet.peers.get(String(id));
  if (!s) return;
  const queued = s.ice.splice(0);
  queued.forEach(c => s.pc.addIceCandidate(c).catch(() => {}));
}

function addPeerTile(p: PeerMeta): void {
  const grid = $('#meetGrid');
  if (grid.querySelector(`[data-peer="${p.id}"]`)) return;
  grid.insertAdjacentHTML(
    'beforeend',
    `
    <div class="meet-tile" data-peer="${esc(p.id)}">
      <video autoplay playsinline></video>
      <div class="meet-tile-name">${esc(p.name || '…')}</div>
      <button type="button" class="mt-fs" title="Fullscreen">${icon('maximize', 14)}</button>
      <div class="meet-tile-badges">
        <span class="mt-badge mt-hand hidden">${icon('hand', 13)}</span>
        <span class="mt-badge mt-live hidden">LIVE</span>
        <span class="mt-badge mt-muted hidden">${icon('muteMic', 13)}</span>
        <span class="mt-badge mt-camoff hidden">${icon('detachCam', 13)}</span>
        <span class="mt-badge mt-share hidden">${icon('monitor', 13)}</span>
      </div>
    </div>`
  );
  const fs = grid.querySelector<HTMLElement>(`[data-peer="${p.id}"] .mt-fs`);
  if (fs)
    fs.addEventListener('click', () =>
      fullscreenTile(grid.querySelector<HTMLVideoElement>(`[data-peer="${p.id}"] video`))
    );
  const st = meet.peers.get(String(p.id));
  if (st) st.name = p.name || st.name;
  updatePeerTile(p.id, { mic: p.mic, cam: p.cam, hand: p.hand, share: p.share });
}

interface PeerFlags {
  mic?: boolean;
  cam?: boolean;
  hand?: boolean;
  share?: boolean;
}

function updatePeerTile(id: number | string, flags: PeerFlags): void {
  const tile = $('#meetGrid').querySelector<HTMLElement>(`[data-peer="${String(id)}"]`);
  if (!tile) return;
  if (typeof flags.mic === 'boolean') {
    const m = tile.querySelector<HTMLElement>('.mt-muted');
    if (m) m.classList.toggle('hidden', flags.mic);
  }
  if (typeof flags.cam === 'boolean') {
    const c = tile.querySelector<HTMLElement>('.mt-camoff');
    if (c) c.classList.toggle('hidden', flags.cam);
  }
  if (typeof flags.hand === 'boolean') {
    const h = tile.querySelector<HTMLElement>('.mt-hand');
    if (h) h.classList.toggle('hidden', !flags.hand);
  }
  if (typeof flags.share === 'boolean') {
    const s = tile.querySelector<HTMLElement>('.mt-share');
    if (s) s.classList.toggle('hidden', !flags.share);
  }
  const st = meet.peers.get(String(id));
  if (st) {
    if (typeof flags.mic === 'boolean') st.mic = flags.mic;
    if (typeof flags.cam === 'boolean') st.cam = flags.cam;
    if (typeof flags.hand === 'boolean') st.hand = flags.hand;
  }
}

function destroyPeer(id: number | string): void {
  const key = String(id);
  const st = meet.peers.get(key);
  if (st) {
    try {
      st.pc.close();
    } catch {
      /* ignore */
    }
    meet.peers.delete(key);
  }
  meet.audioLevels.delete(key);
  const tile = $('#meetGrid').querySelector<HTMLElement>(`[data-peer="${key}"]`);
  if (tile) tile.remove();
  if (meet.pipActive) renderPip();
  if (meet.room) $('#meetOnline').textContent = `${meet.peers.size + 1} in room`;
  maybeEndDirectCall();
}

// In a direct call, when the last remote peer drops (hung up, tab closed,
// or connection lost) the call is over: leave the room and return to the
// screen the user was on before the call.
function maybeEndDirectCall(): void {
  if (meet.room && meet.room.startsWith('direct-') && meet.peers.size === 0) {
    const room = meet.room;
    leaveRoom();
    if (room) clearMeetChat(room);
    returnToPrevView();
  }
}

// ── Active speaker detection (live highlight) ─────────
function attachPeerMeter(id: number | string, stream: MediaStream): void {
  if (meet.noAudio) return;
  if (!stream.getAudioTracks().length) return;
  try {
    if (!meet.audioCtx) {
      const AC: typeof AudioContext =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      meet.audioCtx = new AC();
    }
    const src = meet.audioCtx.createMediaStreamSource(stream);
    const analyser = meet.audioCtx.createAnalyser();
    analyser.fftSize = 128;
    src.connect(analyser);
    meet.audioLevels.set(String(id), { analyser, data: new Uint8Array(analyser.frequencyBinCount) });
    startSpeakerDetection();
  } catch {
    /* audio context not available */
  }
}

function attachSelfMeter(): void {
  if (meet.noAudio || !meet.localStream) return;
  if (!meet.localStream.getAudioTracks().length) return;
  attachPeerMeter('self', meet.localStream);
}

function startSpeakerDetection(): void {
  if (meet.speakerTimer) return;
  meet.speakerTimer = window.setInterval(() => {
    const levels = new Map<string, number>();
    meet.audioLevels.forEach((m, id) => {
      m.analyser.getByteFrequencyData(m.data);
      let sum = 0;
      for (let i = 0; i < m.data.length; i++) sum += m.data[i];
      levels.set(id, sum / m.data.length);
    });
    const talking = [...levels.entries()]
      .filter(([, lv]) => lv > 28)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);
    meet.speakingNow = talking.slice(0, 4);
    let bestId: string | null = null,
      best = 0;
    levels.forEach((lv, id) => {
      if (lv > best) {
        best = lv;
        bestId = id;
      }
    });
    const speaker = best > 28 ? bestId : null;
    if (speaker !== meet.currentSpeaker) {
      meet.currentSpeaker = speaker;
      $$<HTMLElement>('.meet-tile').forEach(t => {
        const isSp = speaker != null && t.dataset.peer === speaker;
        t.classList.toggle('speaking', isSp);
        const live = t.querySelector<HTMLElement>('.mt-live');
        if (live) live.classList.toggle('hidden', !isSp);
      });
    }
    if (meet.pipActive) renderPip();
  }, 450);
}

function resetSpeakerDetection(): void {
  if (meet.speakerTimer) {
    clearInterval(meet.speakerTimer);
    meet.speakerTimer = null;
  }
  meet.audioLevels.clear();
  meet.speakingNow = [];
  meet.currentSpeaker = null;
}

// ── Host controls ──────────────────────────────────────
function renderApprovalPanel(): void {
  const panel = $('#meetApprovalPanel');
  const ul = $('#meetApprovalList');
  if (!meet.requests.length) {
    panel.classList.add('hidden');
    return;
  }
  panel.classList.remove('hidden');
  ul.innerHTML = meet.requests
    .map(
      r => `
    <li>
      ${avatarHtml((r.name || '?').slice(0, 2).toUpperCase(), Number(r.from) || 0, null)}
      <div class="item-main"><div class="item-title">${esc(r.name)}</div><div class="item-sub">Wants to join ${esc(meetRoomLabel(meet.room || ''))}</div></div>
      <div class="item-right tc-actions">
        <button class="btn btn-primary btn-sm" data-approve="${esc(String(r.from))}" data-name="${esc(r.name)}">Allow</button>
        <button class="btn btn-danger btn-sm" data-deny="${esc(String(r.from))}">Deny</button>
      </div>
    </li>`
    )
    .join('');
  $$<HTMLElement>('#meetApprovalList [data-approve]').forEach(btn =>
    btn.addEventListener('click', () => {
      if (meet.ws)
        meet.ws.send(
          JSON.stringify({ type: 'approval', from: meet.selfId, action: 'allow', target: btn.dataset.approve })
        );
      meet.requests = meet.requests.filter(x => x.from !== Number(btn.dataset.approve));
      renderApprovalPanel();
      toast(`Allowed ${btn.dataset.name}`);
    })
  );
  $$<HTMLElement>('#meetApprovalList [data-deny]').forEach(btn =>
    btn.addEventListener('click', () => {
      if (meet.ws)
        meet.ws.send(JSON.stringify({ type: 'approval', from: meet.selfId, action: 'deny', target: btn.dataset.deny }));
      meet.requests = meet.requests.filter(x => x.from !== Number(btn.dataset.deny));
      renderApprovalPanel();
      toast('Join request denied');
    })
  );
}

function hideApprovalPanel(): void {
  $('#meetApprovalPanel').classList.add('hidden');
  $('#meetApprovalList').innerHTML = '';
}

$<HTMLButtonElement>('#meetMic').addEventListener('click', async () => {
  if (meet.noAudio) {
    toast('This meeting is video-only', true);
    return;
  }
  if (meet.media.mic) {
    meet.media.mic = false;
    if (meet.localStream)
      meet.localStream.getAudioTracks().forEach(t => {
        t.enabled = false;
      });
    $('#meetMic').classList.add('off');
    updateSelfTile();
    if (meet.ws)
      meet.ws.send(
        JSON.stringify({ type: 'media-state', from: meet.selfId, mic: false, cam: meet.media.cam, share: meet.sharing })
      );
    return;
  }
  const ok = await enableMeetMedia(true, false);
  if (!ok) return;
  meet.media.mic = true;
  if (meet.localStream)
    meet.localStream.getAudioTracks().forEach(t => {
      t.enabled = true;
    });
  $('#meetMic').classList.remove('off');
  updateSelfTile();
  if (meet.ws)
    meet.ws.send(
      JSON.stringify({ type: 'media-state', from: meet.selfId, mic: true, cam: meet.media.cam, share: meet.sharing })
    );
});

$<HTMLButtonElement>('#meetCam').addEventListener('click', async () => {
  if (meet.media.cam) {
    meet.media.cam = false;
    if (meet.localStream)
      meet.localStream.getVideoTracks().forEach(t => {
        t.enabled = false;
      });
    $('#meetCam').classList.add('off');
    updateSelfTile();
    if (meet.ws)
      meet.ws.send(
        JSON.stringify({ type: 'media-state', from: meet.selfId, mic: meet.media.mic, cam: false, share: meet.sharing })
      );
    return;
  }
  const ok = await enableMeetMedia(false, true);
  if (!ok) return;
  meet.media.cam = true;
  if (meet.localStream)
    meet.localStream.getVideoTracks().forEach(t => {
      t.enabled = true;
    });
  $('#meetCam').classList.remove('off');
  updateSelfTile();
  if (meet.ws)
    meet.ws.send(
      JSON.stringify({ type: 'media-state', from: meet.selfId, mic: meet.media.mic, cam: true, share: meet.sharing })
    );
});

async function enableMeetMedia(audio: boolean, video: boolean): Promise<boolean> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    toast('Media is not supported in this browser', true);
    return false;
  }
  if (meet.localStream) {
    const track = video ? meet.localStream.getVideoTracks()[0] : meet.localStream.getAudioTracks()[0];
    if (track) return true;
  }
  const constraints: MediaStreamConstraints = {};
  if (audio) constraints.audio = true;
  if (video) constraints.video = true;
  let acquired: MediaStream;
  try {
    acquired = await navigator.mediaDevices.getUserMedia(constraints);
  } catch (e) {
    toast('Could not enable ' + (video ? 'camera' : 'microphone') + ': ' + (e as Error).message, true);
    return false;
  }
  if (!meet.localStream) {
    meet.localStream = acquired;
  } else {
    acquired.getTracks().forEach(t => meet.localStream!.addTrack(t));
  }
  const newTracks = acquired.getTracks();
  if (newTracks.length) {
    meet.peers.forEach((s, id) => {
      newTracks.forEach(t => {
        try {
          s.pc.addTrack(t, meet.localStream!);
        } catch {
          /* ignore */
        }
      });
      renegotiate(s.pc, id);
    });
  }
  renderMeetSelf();
  return true;
}

$<HTMLButtonElement>('#meetShare').addEventListener('click', async () => {
  if (meet.sharing) {
    stopSharing();
    return;
  }
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
  } catch {
    toast('Screen share cancelled or unavailable', true);
    return;
  }
  const screenTrack = stream.getVideoTracks()[0];
  if (!screenTrack) {
    toast('No screen track available', true);
    return;
  }
  meet.sharing = true;
  meet.shareStream = stream;
  $('#meetShare').classList.add('off');
  $('#meetShare').title = 'Stop sharing your screen';
  await replaceLocalVideo(screenTrack);
  updateSelfTile();
  toast('Sharing your screen', 'info');
  screenTrack.addEventListener('ended', () => {
    if (meet.sharing) stopSharing();
  });
});

function stopSharing(): void {
  if (!meet.sharing) return;
  let camTrack: MediaStreamTrack | null = null;
  if (meet.localStream) camTrack = meet.localStream.getVideoTracks()[0] || null;
  meet.sharing = false;
  if (meet.shareStream) {
    meet.shareStream.getTracks().forEach(t => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    meet.shareStream = null;
  }
  $('#meetShare').classList.remove('off');
  $('#meetShare').title = 'Share your screen';
  if (camTrack) replaceLocalVideo(camTrack);
  updateSelfTile();
  if (meet.ws)
    meet.ws.send(
      JSON.stringify({ type: 'media-state', from: meet.selfId, mic: meet.media.mic, cam: meet.media.cam, share: false })
    );
  toast('Screen sharing stopped', 'info');
}

async function replaceLocalVideo(track: MediaStreamTrack): Promise<void> {
  if (!track) return;
  const jobs: Promise<void>[] = [];
  meet.peers.forEach((s, id) => {
    const sender = s.pc.getSenders().find(x => x.track && x.track.kind === 'video');
    if (sender) jobs.push(sender.replaceTrack(track).then(() => renegotiate(s.pc, id)));
  });
  try {
    await Promise.all(jobs);
  } catch {
    /* best effort */
  }
}

function fullscreenTile(video: HTMLVideoElement | null): void {
  if (!video) return;
  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => {});
  } else {
    video.requestFullscreen().catch(() => toast('Fullscreen not supported', true));
  }
}

async function renegotiate(pc: RTCPeerConnection, id: string): Promise<void> {
  if (pc.signalingState !== 'stable') return;
  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    if (meet.ws)
      meet.ws.send(JSON.stringify({ type: 'offer', from: meet.selfId, to: String(id), sdp: pc.localDescription!.sdp }));
  } catch {
    /* best effort */
  }
}

$<HTMLButtonElement>('#meetHand').addEventListener('click', () => {
  meet.handUp = !meet.handUp;
  $('#meetHand').classList.toggle('off', meet.handUp);
  updateSelfTile();
  if (meet.ws) meet.ws.send(JSON.stringify({ type: 'hand-state', from: meet.selfId, hand: meet.handUp }));
});

$<HTMLButtonElement>('#meetMuteAll').addEventListener('click', () => {
  if (!meet.isOrganizer) {
    toast('Only the organizer can mute the room', true);
    return;
  }
  if (meet.ws) meet.ws.send(JSON.stringify({ type: 'mute-all', from: meet.selfId }));
  toast('Muted everyone', 'info');
});

$<HTMLButtonElement>('#meetInvite').addEventListener('click', () => {
  const card = openModal(`
    <div class="modal-head"><h2>Invite a participant</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
    <p class="sub">Pull someone into <strong>${esc(meetRoomLabel(meet.room || ''))}</strong>. They'll see an incoming call in Meet.</p>
    <ul id="inviteMemberList" class="plain-list" style="margin-top:14px"></ul>`);
  const listEl = card.querySelector<HTMLElement>('#inviteMemberList');
  if (!listEl) return;
  const available = activeMembers().filter(m => m.id !== state.user?.id && !meet.peers.has(String(m.id)));
  if (!available.length) {
    listEl.innerHTML = emptyHtml(icon('users', 30), 'Everyone is already here', '');
    return;
  }
  listEl.innerHTML = available
    .map(m => {
      const n = m.name || m.username;
      return `<li>
      ${avatarHtml(n.slice(0, 2).toUpperCase(), m.id, m.avatar || null)}
      <div class="item-main"><div class="item-title">${esc(n)}</div></div>
      <div class="item-right"><button class="btn btn-primary btn-sm" data-invite-target="${m.id}">Pull in</button></div>
    </li>`;
    })
    .join('');
  $$<HTMLElement>('#inviteMemberList [data-invite-target]').forEach(btn =>
    btn.addEventListener('click', async () => {
      try {
        await api(`/api/meetings/${encodeURIComponent(meet.room || '')}/invites`, 'POST', {
          to_user: Number(btn.dataset.inviteTarget)
        });
        toast('Invitation sent — they can join from Meet');
        closeModal();
      } catch (err) {
        toast((err as Error).message, true);
      }
    })
  );
});

$<HTMLButtonElement>('#meetLeave').addEventListener('click', () => {
  const room = meet.room;
  leaveRoom();
  if (room) clearMeetChat(room);
  returnToPrevView();
});

function returnToPrevView(): void {
  const prev = meet.prevView;
  meet.prevView = null;
  if (prev && prev !== 'meet' && state.view === 'meet') go(prev);
}

function toggleMeetChat(): void {
  const open = !$('#meetChatPanel').classList.contains('hidden');
  $('#meetChatPanel').classList.toggle('hidden', open);
  $('#meetChatBtn').classList.toggle('on', !open);
  if (!open) {
    if ((!meet.chatWs || meet.chatWs.readyState !== WebSocket.OPEN) && meet.room) {
      connectMeetChat(meet.room);
    }
    $<HTMLTextAreaElement>('#meetChatInput').focus();
  }
}
$<HTMLButtonElement>('#meetChatBtn').addEventListener('click', toggleMeetChat);
$<HTMLButtonElement>('#meetChatClose').addEventListener('click', toggleMeetChat);
setMeetChatReconnect(() => {
  if (meet.room) connectMeetChat(meet.room);
});

export function leaveRoom(): void {
  clearRingTimer();
  meet.ringRoom = null;
  meet.enterSeq++;
  meet.room = null;
  resetSpeakerDetection();
  meet.pipDismissed = false;
  pipHide();
  if (meet.shareStream) {
    meet.shareStream.getTracks().forEach(t => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    meet.shareStream = null;
  }
  meet.sharing = false;
  $('#meetShare').classList.remove('off');
  $('#meetShare').title = 'Share your screen';
  if (meet.audioCtx) {
    try {
      meet.audioCtx.close();
    } catch {
      /* ignore */
    }
    meet.audioCtx = null;
  }
  meet.audioLevels.clear();
  meet.peers.forEach((_, id) => destroyPeer(id));
  if (meet.ws) {
    try {
      meet.ws.close();
    } catch {
      /* ignore */
    }
    meet.ws = null;
  }
  closeMeetChat();
  if (meet.localStream) {
    meet.localStream.getTracks().forEach(t => t.stop());
    meet.localStream = null;
  }
  meet.selfId = null;
  meet.channel = null;
  meet.organizerId = null;
  meet.isOrganizer = false;
  meet.media = { mic: true, cam: true };
  meet.noAudio = false;
  meet.handUp = false;
  meet.requests = [];
  meet.waitingApproval = false;
  $('#meetHand').classList.remove('off');
  $('#meetMic').classList.remove('off');
  $('#meetMic').classList.remove('hidden');
  $('#meetCam').classList.remove('off');
  $('#meetMuteAll').classList.add('hidden');
  $('#meetGrid').innerHTML = '';
  $('#meetChatMessages').innerHTML = '';
  $('#meetChatPanel').classList.remove('hidden');
  $('#meetRoom').classList.add('hidden');
  $('#meetLobby').classList.remove('hidden');
  hideApprovalPanel();
  $('#meetWaitingOverlay').classList.add('hidden');
  loadMeet();
}

export function closeMeet(): void {
  clearRingTimer();
  meet.ringRoom = null;
  meet.enterSeq++;
  meet.room = null;
  resetSpeakerDetection();
  meet.pipDismissed = false;
  pipHide();
  if (meet.shareStream) {
    meet.shareStream.getTracks().forEach(t => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    meet.shareStream = null;
  }
  meet.sharing = false;
  if (meet.audioCtx) {
    try {
      meet.audioCtx.close();
    } catch {
      /* ignore */
    }
    meet.audioCtx = null;
  }
  meet.audioLevels.clear();
  meet.peers.forEach((_, id) => destroyPeer(id));
  if (meet.ws) {
    try {
      meet.ws.close();
    } catch {
      /* ignore */
    }
    meet.ws = null;
  }
  closeMeetChat();
  if (meet.localStream) {
    meet.localStream.getTracks().forEach(t => t.stop());
    meet.localStream = null;
  }
  meet.channel = null;
  $('#meetGrid').innerHTML = '';
}

// ── Meeting chat (live + persisted) ─────────────────────────
const meetChatForm = $<HTMLFormElement>('#meetChatForm');
meetChatForm.addEventListener('submit', e => {
  e.preventDefault();
  sendComposerMessage('meet');
});

const meetChatInput = $<HTMLTextAreaElement>('#meetChatInput');
if (meetChatInput) {
  attachMentionAutocomplete(meetChatInput, () => state.members);
  setupChatTextarea(meetChatInput, meetChatForm);
}

let meetChatRetry: number | null = null;

function connectMeetChat(room: string): void {
  if (!room) return;
  if (meetChatRetry) {
    clearTimeout(meetChatRetry);
    meetChatRetry = null;
  }
  const channel = 'meeting:' + encodeURIComponent(room);
  meet.channel = channel;
  if (meet.chatWs) {
    meet.chatWs.onclose = null;
    try {
      meet.chatWs.close();
    } catch {
      /* ignore */
    }
    meet.chatWs = null;
  }
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}/api/chat?token=${encodeURIComponent(state.token || '')}&channel=${encodeURIComponent(channel)}`;
  try {
    meet.chatWs = new WebSocket(url);
  } catch {
    return;
  }
  meet.chatWs.onopen = () => {
    const el = $('#meetChatMessages');
    if (el && !el.children.length) el.innerHTML = '<div class="chat-empty">Loading history…</div>';
  };
  meet.chatWs.onmessage = ev => {
    let msg: ChatWSInbound;
    try {
      msg = JSON.parse(ev.data as string);
    } catch {
      return;
    }
    const el = $('#meetChatMessages');
    if (!el) return;
    if (msg.type === 'history') {
      el.innerHTML =
        (msg.messages || []).map(renderChatMsg).join('') || '<div class="chat-empty">No messages yet — say hi!</div>';
      el.scrollTop = el.scrollHeight;
    } else if (msg.type === 'message') {
      if (el.querySelector('.chat-empty')) el.innerHTML = '';
      el.insertAdjacentHTML('beforeend', renderChatMsg(msg));
      el.scrollTop = el.scrollHeight;
    } else if (msg.type === 'cleared') {
      el.innerHTML = '<div class="chat-empty">Chat cleared.</div>';
    }
  };
  meet.chatWs.onclose = () => {
    meet.chatWs = null;
    // Keep the meeting chat alive: auto-reconnect while still in the room.
    if (meet.room === room && state.view === 'meet') {
      meetChatRetry = window.setTimeout(() => connectMeetChat(room), 3000);
    }
  };
  meet.chatWs.onerror = () => {};
}

function closeMeetChat(): void {
  if (meetChatRetry) {
    clearTimeout(meetChatRetry);
    meetChatRetry = null;
  }
  if (meet.chatWs) {
    meet.chatWs.onclose = null;
    try {
      meet.chatWs.close();
    } catch {
      /* ignore */
    }
    meet.chatWs = null;
  }
}

// The meeting is over — wipe the persisted chat history for the room so
// re-joining starts fresh and the saved-history view is empty.
function clearMeetChat(room: string): void {
  api('/api/chat/clear', 'POST', { channel: 'meeting:' + encodeURIComponent(room) }).catch(() => {
    /* best-effort */
  });
}

// ── Picture-in-picture (meeting keeps running off-tab) ──────
function pipShow(): void {
  const el = $('#meetPip');
  if (!el || !meet.room || meet.pipActive) return;
  if (meet.pipDismissed) return;
  meet.pipActive = true;
  const roomEl = $('#meetPipRoom');
  if (roomEl) roomEl.textContent = meetRoomLabel(meet.room);
  el.classList.remove('hidden');
  renderPip();
}

function pipHide(): void {
  meet.pipActive = false;
  const el = $('#meetPip');
  if (el) el.classList.add('hidden');
}

function pipDismiss(): void {
  meet.pipDismissed = true;
  pipHide();
}

function renderPip(): void {
  const grid = $('#meetPipGrid');
  if (!grid) return;
  const ids = meet.speakingNow.slice(0, 4);
  const existing = new Map<string, HTMLElement>();
  grid.querySelectorAll<HTMLElement>('[data-pip]').forEach(t => existing.set(t.dataset.pip || '', t));
  existing.forEach((t, id) => {
    if (!ids.includes(id)) t.remove();
  });
  ids.forEach(id => {
    let tile = existing.get(id);
    if (tile) {
      existing.delete(id);
    } else {
      tile = document.createElement('div');
      tile.className = 'pip-tile';
      tile.dataset.pip = id;
      grid.appendChild(tile);
    }
    renderPipTile(tile, id);
  });
  const empty = grid.querySelector<HTMLElement>('.pip-empty');
  if (!ids.length) {
    if (!empty) {
      const p = document.createElement('div');
      p.className = 'pip-empty';
      p.textContent = 'No one is talking';
      grid.appendChild(p);
    }
  } else if (empty) {
    empty.remove();
  }
}

function renderPipTile(tile: HTMLElement, id: string): void {
  const st = meet.peers.get(id);
  const isSelf = id === 'self';
  const name = isSelf ? 'You' : st?.name || '…';
  const cam = isSelf ? meet.media.cam : !!st?.cam;
  const mic = isSelf ? meet.media.mic : !!st?.mic;
  const stream = isSelf ? meet.localStream : st?.stream || null;
  const hasVideo = cam && !!stream && stream.getVideoTracks().some(t => t.enabled);

  if (hasVideo) {
    if (!tile.querySelector('video')) {
      tile.innerHTML = '';
      const v = document.createElement('video');
      v.autoplay = true;
      v.playsInline = true;
      v.muted = isSelf;
      tile.appendChild(v);
    }
    const v = tile.querySelector<HTMLVideoElement>('video');
    if (v && (!v.srcObject || v.srcObject !== stream)) v.srcObject = stream;
    tile.classList.add('has-video');
  } else {
    if (!tile.querySelector('.pip-avatar')) {
      tile.innerHTML = '';
      const av = document.createElement('span');
      av.className = 'pip-avatar';
      av.textContent = (name || '?').slice(0, 2).toUpperCase();
      tile.appendChild(av);
    }
    tile.classList.remove('has-video');
  }

  tile.classList.toggle('self', isSelf);
  tile.classList.toggle('muted', !mic);

  let nameEl = tile.querySelector<HTMLElement>('.pip-name');
  if (!nameEl) {
    nameEl = document.createElement('span');
    nameEl.className = 'pip-name';
    tile.appendChild(nameEl);
  }
  nameEl.textContent = name;

  let badge = tile.querySelector<HTMLElement>('.pip-badge');
  const muted = !mic;
  if (muted) {
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'pip-badge';
      tile.appendChild(badge);
    }
    badge.innerHTML = icon('muteMic', 10);
  } else if (badge) {
    badge.remove();
  }
}

setOnViewChanged(tab => {
  if (tab === 'meet') {
    meet.pipDismissed = false;
    pipHide();
  } else if (meet.room && meet.selfId != null) {
    pipShow();
  }
});

const pipEl = $('#meetPip');
if (pipEl) {
  let pipDrag = false;
  let pipMoved = false;
  let pipDragX = 0;
  let pipDragY = 0;

  pipEl.addEventListener('pointerdown', e => {
    if ((e.target as HTMLElement).closest('#meetPipClose')) return;
    pipDrag = true;
    pipMoved = false;
    pipDragX = e.clientX;
    pipDragY = e.clientY;
    pipEl.setPointerCapture(e.pointerId);
  });
  pipEl.addEventListener('pointermove', e => {
    if (!pipDrag) return;
    const dx = e.clientX - pipDragX;
    const dy = e.clientY - pipDragY;
    if (Math.abs(dx) + Math.abs(dy) > 4) pipMoved = true;
    const rect = pipEl.getBoundingClientRect();
    const left = Math.min(Math.max(Math.round(rect.left + dx), 0), window.innerWidth - rect.width);
    const top = Math.min(Math.max(Math.round(rect.top + dy), 0), window.innerHeight - rect.height);
    pipEl.style.left = `${left}px`;
    pipEl.style.top = `${top}px`;
    pipEl.style.bottom = 'auto';
    pipDragX = e.clientX;
    pipDragY = e.clientY;
  });
  const pipEnd = () => {
    pipDrag = false;
  };
  pipEl.addEventListener('pointerup', pipEnd);
  pipEl.addEventListener('pointercancel', pipEnd);
  pipEl.addEventListener('click', e => {
    if ((e.target as HTMLElement).closest('#meetPipClose')) {
      e.stopPropagation();
      pipDismiss();
      return;
    }
    if (pipMoved) {
      e.stopPropagation();
      pipMoved = false;
      return;
    }
    go('meet');
  });
}

// ── Incoming call popup (answer / reject / message) ──────────
let callPopupOpen = false;
let currentCall: MeetingInvite | null = null;

function callerName(inv: MeetingInvite): string {
  return inv.from_name || inv.from_username || 'A teammate';
}

function showCallPopup(inv: MeetingInvite): void {
  if (callPopupOpen) return;
  meet._notifiedInviteIds.push(inv.id);
  // Already in a call — don't cover the whole page with the popup (blocks
  // hover/click on the rest of the app). Notify with a toast instead.
  if (meet.room) {
    toast(`${callerName(inv)} is calling you to “${meetRoomLabel(inv.room)}”`, 'info', 6000);
    return;
  }
  currentCall = inv;
  callPopupOpen = true;

  playRingTone();
  broadcastIncomingCall(inv);

  const name = callerName(inv);
  const av = $('#callAvatar');
  if (av) av.innerHTML = avatarHtml(name.slice(0, 2).toUpperCase(), inv.from_user, inv.from_avatar || null);
  const nm = $('#callName');
  if (nm) nm.textContent = name;
  const rm = $('#callRoom');
  if (rm) rm.textContent = `is calling you to “${meetRoomLabel(inv.room)}”`;
  const box = $('#callMessageForm');
  if (box) box.classList.add('hidden');
  const reject = $('#callReject');
  if (reject) reject.classList.remove('hidden');
  const inp = $<HTMLInputElement>('#callMessageInput');
  if (inp) inp.value = '';
  const overlay = $('#callPopup');
  if (overlay) overlay.classList.remove('hidden');
}

function closeCallPopup(): void {
  const overlay = $('#callPopup');
  if (overlay) overlay.classList.add('hidden');
  stopRingTone();
  if (currentCall) broadcastCallCancelled(currentCall.id);
  callPopupOpen = false;
  currentCall = null;
}

let answeringCall = false;
async function answerCall(): Promise<void> {
  if (answeringCall) return;
  answeringCall = true;
  const inv = currentCall;
  if (!inv) {
    answeringCall = false;
    return;
  }
  stopRingTone();
  broadcastCallCancelled(inv.id);
  const overlay = $('#callPopup');
  if (overlay) overlay.classList.add('hidden');
  try {
    const res = await api<{ room: string }>(`/api/meetings/invites/${inv.id}`, 'POST');
    callPopupOpen = false;
    currentCall = null;
    meet.prevView = state.view;
    if (meet.room) leaveRoom();
    go('meet');
    enterRoom(res.room);
  } catch (err) {
    toast((err as Error).message, true);
    callPopupOpen = false;
    currentCall = null;
  }
  answeringCall = false;
}

let decliningCall = false;
async function declineCall(message: string): Promise<void> {
  if (decliningCall) return;
  decliningCall = true;
  const inv = currentCall;
  if (!inv) {
    decliningCall = false;
    return;
  }
  try {
    await api(`/api/meetings/invites/${inv.id}/decline`, 'POST', { message });
    toast(message ? 'Sent them a message' : 'Call declined');
  } catch (err) {
    toast((err as Error).message, true);
  }
  closeCallPopup();
  decliningCall = false;
  if (state.view === 'meet' && !meet.room) loadMeet();
}

$('#callReject').addEventListener('click', () => {
  const box = $('#callMessageForm');
  const reject = $('#callReject');
  if (!box || !reject) return;
  if (box.classList.contains('hidden')) {
    box.classList.remove('hidden');
    reject.classList.add('hidden');
    $<HTMLInputElement>('#callMessageInput').focus();
  }
});
$('#callMessageSkip').addEventListener('click', () => declineCall(''));
$('#callAnswer').addEventListener('click', answerCall);
$('#callClose').addEventListener('click', closeCallPopup);
$('#callOverlay').addEventListener('click', closeCallPopup);
$<HTMLFormElement>('#callMessageForm').addEventListener('submit', e => {
  e.preventDefault();
  const inp = $<HTMLInputElement>('#callMessageInput');
  declineCall((inp.value || '').trim());
});

// Initialize broadcast channel first for cross-tab call notifications
initBroadcastChannel();

// Poll for incoming invites on every screen while logged in.
window.setInterval(async () => {
  if (!state.token || !state.user) return;
  try {
    const invites = await api<MeetingInvite[]>('/api/meetings/invites');
    meet.myInvites = invites || [];
    const ids = (invites || []).map(i => i.id);
    // The caller hung up or the call timed out — stop ringing if our open
    // call popup is no longer pending.
    if (currentCall && !ids.includes(currentCall.id)) closeCallPopup();
    meet._notifiedInviteIds = meet._notifiedInviteIds.filter(id => ids.includes(id));
    if (state.view === 'meet' && !meet.room) renderMeetInvites(invites || []);
    (invites || []).forEach(i => {
      if (!meet._notifiedInviteIds.includes(i.id)) showCallPopup(i);
    });
  } catch {
    /* offline */
  }
}, 5000);

registerView('meet', loadMeet);
